import Box from '@mui/material/Box';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Dayjs } from 'dayjs';
import React from 'react';
import DateSelector from './DateSelector';
import TDateRange from './TDateRange';

export interface DateRangeSelectorProps {
  dates: TDateRange;
  disabled?: boolean;
  inputFormat?: string;
  // eslint-disable-next-line no-unused-vars
  onDateChange: (e: TDateRange) => void;
}

const rootSx = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  '& .MuiInputBase-root': {
    width: 'fit-content',
    // overwrites the color
    color: `${theme.palette.primary.main} !important`,
  },
  '& .MuiInputBase-input': {
    maxWidth: theme.spacing(11.25),
  },
});

const textSx: SxProps = {
  width: 'auto',
  color: 'text.disabled',
  fontWeight: 'bold',
  p: 1,
  pr: 1.5,
};

const DateRangeSelector: React.FC<React.PropsWithChildren<DateRangeSelectorProps>> = (props) => {
  const {
    dates, disabled, inputFormat, onDateChange,
  } = props;
  const theme = useTheme();

  const _handleDateChange = (newDate: Dayjs, position: 'start' | 'end') => {
    onDateChange(
      position === 'start'
        ? {
          start: newDate.isAfter(dates?.end as Dayjs)
            ? (dates?.end as Dayjs)
            : newDate,
          end: newDate.isAfter(dates?.end as Dayjs)
            ? newDate
            : (dates?.end as Dayjs),
        }
        : {
          end: newDate.isBefore(dates?.start as Dayjs)
            ? (dates?.start as Dayjs)
            : newDate,
          start: newDate.isBefore(dates?.start as Dayjs)
            ? newDate
            : (dates?.start as Dayjs),
        },
    );
  };

  return (
    <Box sx={rootSx(theme)}>
      <DateSelector
        data-testid="start"
        disabled={disabled}
        inputFormat={inputFormat}
        onChange={(e) => dates && _handleDateChange(e as Dayjs, 'start')}
        value={dates.start}
      />
      <Typography
        data-testid="text"
        component="span"
        variant="body2"
        sx={textSx}
      >
        To
      </Typography>
      <DateSelector
        data-testid="end"
        disabled={disabled}
        inputFormat={inputFormat}
        onChange={(e) => dates && _handleDateChange(e as Dayjs, 'end')}
        value={dates.end}
      />
    </Box>
  );
};

export default DateRangeSelector;
